import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../Includes/Navbar/Navbar";
import Sidebar from "../../Includes/Sidebar/Sidebar";
import UserServices from "../../../UserServices/UserServices";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// Fetch utility using UserServices
const fetchAttendance = async ({
  start_date,
  end_date,
  branch_id = "",
  gender = "",
  start_time = "",
  end_time = "",
  limit = 100,
  page = 1,
}) => {
  try {
    const resp = await UserServices.getClientAttendence(
      "Member",
      "1",
      branch_id,
      {
        sdate: start_date,
        edate: end_date,
        start_time,
        end_time,
        gender,
      },
      "",
      limit,
      page
    );
    if (resp.status === 200) {
      return resp.data.data?.data || [];
    }
    return [];
  } catch (e) {
    return [];
  }
};

const formatBranch = (uid = "") => {
  const m = uid.match(/^([A-Za-z]+)(\d+)/);
  return m ? `${m[1]}-${m[2]}` : uid;
};

const formatDate = (iso = "") => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const to12h = (time24 = "") => {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  let hour = parseInt(h, 10);
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${m} ${suffix}`;
};

const COLORS = ["#0088FE", "#FF6384", "#00C49F", "#FFBB28"]; // male, female, etc

export default function FootfallGenderReport({ initialFilters, embedded = false }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = useState(initialFilters || {
    start_date: today,
    end_date: today,
    branch_id: "",
    gender: "",
    start_time: "",
    end_time: "",
    limit: 25,
    page: 1,
  });
  const [tempFilters, setTempFilters] = useState(() => ({ ...filters }));
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch data (supports multi-day by fetching each date)
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const multiDay = filters.start_date !== filters.end_date;
        if (!multiDay) {
          const data = await fetchAttendance({ ...filters });
          setRaw(data);
        } else {
          const getDateList = (startIso, endIso) => {
            const out = [];
            let cur = new Date(startIso);
            const end = new Date(endIso);
            while (cur <= end) {
              const iso = cur.toISOString().slice(0, 10);
              out.push(iso);
              cur.setDate(cur.getDate() + 1);
            }
            return out;
          };
          const days = getDateList(filters.start_date, filters.end_date);
          const results = await Promise.all(
            days.map((d) =>
              fetchAttendance({
                ...filters,
                start_date: d,
                end_date: d,
                page: 1,
                limit: 1000,
              })
            )
          );
          const merged = results.flat();
          setRaw(merged);
        }
      } catch (_e) {
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  // Aggregations and chart data
  const { total, maleCount, femaleCount, pieData, byKeyData, perDateHourly } = useMemo(() => {
    const total = raw.length;
    const maleCount = raw.filter((r) => (r.gender || "").toLowerCase() === "male").length;
    const femaleCount = raw.filter((r) => (r.gender || "").toLowerCase() === "female").length;

    const pieData = [
      { name: "Male", value: maleCount },
      { name: "Female", value: femaleCount },
    ];

    const singleDay = filters.start_date === filters.end_date;
    const keyFn = (r) => (singleDay ? r.checkin_time_24h?.slice(0, 2) || "" : r.date || "");
    const map = new Map();
    raw.forEach((r) => {
      const k = keyFn(r);
      if (!k) return;
      const cur = map.get(k) || { key: k, Male: 0, Female: 0 };
      const g = (r.gender || "").toLowerCase();
      if (g === "male") cur.Male += 1;
      if (g === "female") cur.Female += 1;
      map.set(k, cur);
    });
    let byKeyData = Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
    if (!singleDay) byKeyData = byKeyData.map((d) => ({ ...d, key: formatDate(d.key) }));
    else byKeyData = byKeyData.map((d) => ({ ...d, key: to12h(`${d.key}:00`) }));

    // Per-date hourly map for multi-day
    const perDateHourly = {};
    if (!singleDay) {
      raw.forEach((r) => {
        const d = r.date || "";
        if (!d) return;
        const hour = (r.checkin_time_24h || "00:00:00").slice(0, 2);
        const key = to12h(`${hour}:00`);
        const g = (r.gender || "").toLowerCase();
        perDateHourly[d] = perDateHourly[d] || {};
        const row = perDateHourly[d][key] || { key, Male: 0, Female: 0 };
        if (g === "male") row.Male += 1;
        if (g === "female") row.Female += 1;
        perDateHourly[d][key] = row;
      });
      Object.keys(perDateHourly).forEach((d) => {
        perDateHourly[d] = Object.values(perDateHourly[d]).sort((a, b) => a.key.localeCompare(b.key));
      });
    }
    return { total, maleCount, femaleCount, pieData, byKeyData, perDateHourly };
  }, [raw, filters.start_date, filters.end_date]);

  // Group details by date for multi-day
  const groupedByDate = useMemo(() => {
    const isMulti = filters.start_date !== filters.end_date;
    if (!isMulti) return {};
    const map = {};
    raw.forEach((r) => {
      const d = r.date || "";
      if (!d) return;
      map[d] = map[d] || [];
      map[d].push(r);
    });
    return map;
  }, [raw, filters.start_date, filters.end_date]);

  const perDateCounts = useMemo(() => {
    const counts = {};
    Object.keys(groupedByDate).forEach((d) => {
      const arr = groupedByDate[d] || [];
      const male = arr.filter((r) => (r.gender || "").toLowerCase() === "male").length;
      const female = arr.filter((r) => (r.gender || "").toLowerCase() === "female").length;
      counts[d] = { total: arr.length, male, female };
    });
    return counts;
  }, [groupedByDate]);

  // Handlers
  const onTempChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((f) => ({ ...f, [name]: value }));
  };
  const applyFilters = () => {
    setFilters((prev) => ({ ...prev, ...tempFilters, page: 1 }));
  };

  const exportCsv = () => {
    const headers = [
      "Date",
      "Check-in",
      "Check-out",
      "Branch",
      "Gender",
      "Full Name",
      "Verified_by",
    ];
    const rows = raw.map((r) => [
      formatDate(r.date),
      r.checkin_time_12h,
      r.checkout_time_12h || "",
      formatBranch(r.attendee?.uid || ""),
      r.gender || "",
      `${r.attendee?.first_name || ""} ${r.attendee?.last_name || ""}`.trim(),
      r.verified_by || r.verification_method || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `footfall_gender_${filters.start_date}_${filters.end_date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const paged = raw; // API pagination used when single-day (filters.limit/page)

  const filterBar = (
    <div className="card mb-3">
      <div className="card-body">
        <form className="row g-2" aria-label="Footfall gender filters">
          <div className="col-12 col-md-3">
            <label className="form-label">Start date</label>
            <input name="start_date" type="date" className="form-control" value={tempFilters.start_date} onChange={onTempChange} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">End date</label>
            <input name="end_date" type="date" className="form-control" value={tempFilters.end_date} onChange={onTempChange} />
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">Branch</label>
            <select name="branch_id" className="form-select" value={tempFilters.branch_id} onChange={onTempChange}>
              <option value="">All</option>
              <option value="15">F-11</option>
              <option value="1">G-13</option>
            </select>
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">Gender</label>
            <select name="gender" className="form-select" value={tempFilters.gender} onChange={onTempChange}>
              <option value="">All</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div className="col-6 col-md-1">
            <label className="form-label">Limit</label>
            <input name="limit" type="number" min="5" step="5" className="form-control" value={tempFilters.limit} onChange={onTempChange} />
          </div>
          <div className="col-6 col-md-1 d-flex align-items-end">
            <button type="button" className="btn btn-primary w-100" onClick={applyFilters}>Go</button>
          </div>
          <div className="col-6 col-md-1 d-flex align-items-end">
            <button type="button" className="btn btn-outline-secondary w-100" onClick={exportCsv}>CSV</button>
          </div>
        </form>
      </div>
    </div>
  );

  const summaryAndChart = (
    <>
      {/* Summary Cards */}
      <div className="row g-3">
        <div className="col-12 col-md-3">
          <div className="card h-100"><div className="card-body"><div className="text-muted">Total Entries</div><div className="fs-3 fw-bold">{total}</div></div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card h-100"><div className="card-body"><div className="text-muted">Male</div><div className="fs-4 fw-bold">{maleCount}</div></div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card h-100"><div className="card-body"><div className="text-muted">Female</div><div className="fs-4 fw-bold">{femaleCount}</div></div></div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted mb-2">Male% / Female%</div>
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={60} label>
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="card my-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Male vs Female {filters.start_date === filters.end_date ? "by Hour" : "by Day"}</h6>
            {loading ? <small className="text-muted">Loading…</small> : null}
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={byKeyData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="key" interval={0} tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Male" stackId="g" fill={COLORS[0]} />
                <Bar dataKey="Female" stackId="g" fill={COLORS[1]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );

  const multiDayHourGrid = null;

  const detailSection = (
    filters.start_date === filters.end_date ? (
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Detail</h6>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-light" onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, Number(f.page) - 1) }))}>Prev</button>
              <span className="align-self-center small">Page {filters.page}</span>
              <button type="button" className="btn btn-light" onClick={() => setFilters((f) => ({ ...f, page: Number(f.page) + 1 }))}>Next</button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Branch</th>
                  <th>Gender</th>
                  <th>Full Name</th>
                  <th>Verified_by</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r, idx) => (
                  <tr key={idx}>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.checkin_time_12h}</td>
                    <td>{r.checkout_time_12h || ""}</td>
                    <td>{formatBranch(r.attendee?.uid || "")}</td>
                    <td>{r.gender}</td>
                    <td>{`${r.attendee?.first_name || ""} ${r.attendee?.last_name || ""}`.trim()}</td>
                    <td>{r.verified_by || r.verification_method || ""}</td>
                  </tr>
                ))}
                {!paged.length && (<tr><td colSpan={7} className="text-center text-muted">No records</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    ) : (
      <div className="row g-3">
        {Object.keys(groupedByDate).sort().map((d) => (
          <div className="col-12" key={d}>
            <div className="row g-3">
              {/* Left: Chart (50%) */}
              <div className="col-12 col-lg-6">
                <div className="card h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{formatDate(d)}</h6>
                      <div className="d-flex gap-2">
                        <span className="badge bg-primary">Total: {perDateCounts[d]?.total || groupedByDate[d].length}</span>
                        <span className="badge bg-info text-dark">Male: {perDateCounts[d]?.male || 0}</span>
                        <span className="badge bg-danger">Female: {perDateCounts[d]?.female || 0}</span>
                      </div>
                    </div>
                    <div style={{ width: "100%", height: 280 }}>
                      <ResponsiveContainer>
                        <BarChart data={perDateHourly[d]} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="key" interval={0} tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="Male" stackId="g" fill={COLORS[0]} />
                          <Bar dataKey="Female" stackId="g" fill={COLORS[1]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
              {/* Right: Detail table (50%) */}
              <div className="col-12 col-lg-6">
                <div className="card h-100">
                  <div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: 380, overflowY: 'auto' }}>
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Branch</th>
                            <th>Gender</th>
                            <th>Full Name</th>
                            <th>Verified_by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedByDate[d].map((r, idx) => (
                            <tr key={idx}>
                              <td>{formatDate(r.date)}</td>
                              <td>{r.checkin_time_12h}</td>
                              <td>{r.checkout_time_12h || ""}</td>
                              <td>{formatBranch(r.attendee?.uid || "")}</td>
                              <td>{r.gender}</td>
                              <td>{`${r.attendee?.first_name || ""} ${r.attendee?.last_name || ""}`.trim()}</td>
                              <td>{r.verified_by || r.verification_method || ""}</td>
                            </tr>
                          ))}
                          {!groupedByDate[d].length && (<tr><td colSpan={7} className="text-center text-muted">No records</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  );

  const content = (
    <div className="container-fluid">
      {filterBar}
      {summaryAndChart}
      {multiDayHourGrid}
      {detailSection}
    </div>
  );

  if (embedded) return content;

  return (
    <div className='wrapper'>
      <Navbar/>
      <Sidebar/>
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          {content}
        </div>
      </main>
    </div>
  );
}


